import MuiText from '@mui/material/Typography';
import type { ComponentProps, ElementType, ReactNode } from 'react';

type SxProp = ComponentProps<typeof MuiText>['sx'];
type TypographyProps = Readonly<{
  id?: string;
  variant?: 'heading1' | 'heading2' | 'heading3' | 'inputLabel' | 'body';
  component?: ElementType;
  color?: string;
  sx?: SxProp;
  children: ReactNode;
}>;

export default function Typography({
  variant,
  sx,
  color,
  children,
  ...props
}: TypographyProps) {
  if (variant === 'heading1') {
    return (
      <MuiText
        component="h1"
        sx={{
          color: color ?? 'gray.600',
          fontFamily: 'Roboto',
          fontSize: '2rem',
          fontStyle: 'normal',
          fontWeight: '500',
          lineHeight: '2.5rem',
          ...sx,
        }}
        {...props}
      >
        {children}
      </MuiText>
    );
  }

  if (variant === 'heading2') {
    return (
      <MuiText
        component="h2"
        sx={{
          color: color ?? 'gray.600',
          fontFamily: 'Roboto',
          fontSize: '1.5rem',
          fontStyle: 'normal',
          fontWeight: '500',
          lineHeight: '2rem',
          ...sx,
        }}
        {...props}
      >
        {children}
      </MuiText>
    );
  }

  if (variant === 'heading3') {
    return (
      <MuiText
        component="h3"
        sx={{
          color: color ?? 'gray.600',
          fontFamily: 'Roboto',
          fontSize: '1rem',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '1.5rem',
          ...sx,
        }}
        {...props}
      >
        {children}
      </MuiText>
    );
  }

  if (variant === 'body') {
    return (
      <MuiText
        sx={{
          color: color ?? 'gray.900',
          fontFamily: 'Roboto',
          fontSize: '1rem',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '1.55rem',
          ...sx,
        }}
        {...props}
      >
        {children}
      </MuiText>
    );
  }

  return (
    <Typography variant="body" sx={sx} color={color} {...props}>
      {children}
    </Typography>
  );
}
